/**
 * @file canvas session store
 * @description 画布当次会话的共享元信息与版本令牌
 *
 * - meta：进入画布后稳定不变，部分会用作请求的公参
 * - versionCode：每次保存成功后由服务端返回并替换
 * - serverSchema：服务端最后一次持久化时的 tldraw schema 版本。
 *   每次保存时若客户端 schema 与此不同则随 patch 一并上传，成功后更新。
 */

import {create} from 'zustand';
import type {SerializedSchema} from 'tldraw';
import type {CanvasFromValue} from '@hooks/useGoCanvas';
import type {CanvasRecordMap} from '@/pages/canvas/utils/diffCanvasRecord';
import type {
    CanvasDetailResponse,
    CanvasDetailRequestParams,
    ComicInfo,
    EndpointEntryRequestParams,
    ToolBarInfo,
} from '@/services/canvas/interface';
import type {CanvasTypeValue} from '@/types/canvas';
import type {GeneratorConfigResponse, SelectionValue, StyleSelection} from '@/types/generator';
import type {MediaTypeValue} from '@/types/media';
import {ComicStyleType} from '@/types/project';

export interface CanvasSessionMeta {
    canvasId: string;
    canvasType: CanvasTypeValue;
    name: string;
    toolbarInfo?: ToolBarInfo;
    entryId?: number;
    sourceExt?: Record<string, unknown>;
    comicInfo?: ComicInfo;
    from?: CanvasFromValue;
}

interface CanvasSessionState {
    meta: CanvasSessionMeta | null;
    version: number;
    /**
     * 当前画布会话是否发生版本冲突。
     * 一旦发生，当前页面的本地编辑基线不再可信，只允许刷新后重新拉取最新版本。
     */
    hasVersionConflict: boolean;
    /**
     * 服务端当前持久化的 schema 版本，null 表示服务端尚无存量 schema（新画布或历史数据）。
     * 客户端以此判断是否需要随 patch 上传新 schema。
     */
    schema: SerializedSchema | null;
    /**
     * 进入画布时由 CanvasEngine 注入的 diff 基线
     * - 当本地存在未保存数据（indexedDB 已恢复未同步的改动）时，用服务端 records 作为 reference，useAutoSaveCanvas 挂载后即可 diff 出"未上传的增量"补传到服务端
     * - 无未保存数据时，保持为 null，useAutoSaveCanvas 走默认逻辑（取 editor 当前态做基线）
     * - 一次性使用，被 useAutoSaveCanvas 消费后立即置回 null
     */
    initialReference: CanvasRecordMap | null;
    /**
     * 各创作器类型的配置协议（来源于 canvas/get 接口的 creatorInfo 字段）
     */
    creatorInfo: Partial<Record<MediaTypeValue, GeneratorConfigResponse>>;
    /**
     * 基于 comicInfo 预计算的项目级 preset selections（ratio / style）
     */
    presetSelections: Record<string, SelectionValue>;

    init: (
        payload: CanvasDetailResponse,
        serverSchema?: SerializedSchema,
        requestParams?: CanvasDetailRequestParams,
    ) => void;
    setVersionCode: (code: number) => void;
    setSchema: (schema: SerializedSchema) => void;
    setInitialReference: (ref: CanvasRecordMap | null) => void;
    markVersionConflict: () => void;
    setCreatorInfo: (creatorInfo: Partial<Record<MediaTypeValue, GeneratorConfigResponse>>) => void;
    reset: () => void;
}

/**
 * 基于项目 comicInfo 预计算通用的 preset selections（style / ratio）
 */
function buildPresetSelections(comicInfo: ComicInfo | undefined): Record<string, SelectionValue> {
    if (!comicInfo) {
        return {};
    }

    const preset: Record<string, SelectionValue> = {};

    if (comicInfo.ratio) {
        preset.ratio = comicInfo.ratio;
    }

    if (comicInfo.styleId !== undefined) {
        preset.style = {
            styleId: comicInfo.styleId,
            styleType: comicInfo.styleType ?? ComicStyleType.Official,
            styleName: comicInfo.styleName,
            styleUrl: comicInfo.styleUrl,
        } as StyleSelection;
    }

    return preset;
}

export const useCanvasSessionStore = create<CanvasSessionState>((set, get) => ({
    meta: null,
    version: 0,
    hasVersionConflict: false,
    schema: null,
    initialReference: null,
    creatorInfo: {},
    presetSelections: {},

    init: (payload, initSchema, requestParams) => {
        const sourceExt = (requestParams as {sourceExt?: Record<string, unknown>})?.sourceExt;
        set({
            // 初始化之后就不会再改变
            meta: {
                canvasId: payload.canvasId,
                canvasType: payload.canvasType,
                name: payload.name,
                toolbarInfo: payload.toolbarInfo,
                ...((requestParams as EndpointEntryRequestParams)?.entryId && {
                    entryId: (requestParams as EndpointEntryRequestParams).entryId,
                }),
                from: requestParams?.from,
                ...(sourceExt ? {sourceExt} : null),
                ...(payload.comicInfo ? {comicInfo: payload.comicInfo} : null),
            },
            // 每次保存都会改变
            version: payload.version,
            // 从 snapshot 解析出的 schema，null 表示新画布（无存量 snapshot）
            schema: initSchema ?? null,
            hasVersionConflict: false,
            initialReference: null,
        });
    },

    setVersionCode: (code) => set({version: code}),

    setSchema: (schema) => set({schema: schema}),

    setInitialReference: (ref) => set({initialReference: ref}),

    markVersionConflict: () => set({hasVersionConflict: true}),

    setCreatorInfo: (creatorInfo) => {
        const comicInfo = get().meta?.comicInfo;
        set({creatorInfo, presetSelections: buildPresetSelections(comicInfo)});
    },

    reset: () =>
        set({
            meta: null,
            version: 0,
            schema: null,
            hasVersionConflict: false,
            initialReference: null,
            creatorInfo: {},
            presetSelections: {},
        }),
}));

/**
 * 订阅式读取 meta（组件内使用）
 * 调用前应确保已 init，否则返回 null
 */
export const useCanvasSessionMeta = () => useCanvasSessionStore((s) => s.meta);

/**
 * 订阅式读取 versionCode
 * 仅给需要"感知版本变化"的 UI 使用；发请求时请用 getState 直接取最新值
 */
export const useCanvasVersionCode = () => useCanvasSessionStore((s) => s.version);

export const useHasCanvasVersionConflict = () => useCanvasSessionStore((s) => s.hasVersionConflict);
